// All wording, per language and namespace. Every key exists in exactly one namespace (tests check it), so a lookup
// never depends on namespace order: the app registers every namespace and falls back across all of them.
import enAdmin from './locales/en/admin.js';
import enApp from './locales/en/app.js';
import enCommon from './locales/en/common.js';
import enConnect from './locales/en/connect.js';
import enGlossary from './locales/en/glossary.js';
import enHome from './locales/en/home.js';
import enLabels from './locales/en/labels.js';
import enLogin from './locales/en/login.js';
import enLogs from './locales/en/logs.js';
import enPeople from './locales/en/people.js';
import enSettings from './locales/en/settings.js';
import enStats from './locales/en/stats.js';
import koAdmin from './locales/ko/admin.js';
import koApp from './locales/ko/app.js';
import koCommon from './locales/ko/common.js';
import koConnect from './locales/ko/connect.js';
import koGlossary from './locales/ko/glossary.js';
import koHome from './locales/ko/home.js';
import koLabels from './locales/ko/labels.js';
import koLogin from './locales/ko/login.js';
import koLogs from './locales/ko/logs.js';
import koPeople from './locales/ko/people.js';
import koSettings from './locales/ko/settings.js';
import koStats from './locales/ko/stats.js';

export const LANGS = ['ko', 'en'] as const;
export type Lang = (typeof LANGS)[number];

/**
 * Namespaces: `common` (shell, shared parts, value contract, permission names …), `labels` (label dictionary `label.<key>`),
 * one per mockup screen file, and `app` (product wording the mockup does not have).
 */
export const NAMESPACES = ['common', 'labels', 'admin', 'connect', 'glossary', 'home', 'login', 'logs', 'people', 'settings', 'stats', 'app'] as const;
export type Namespace = (typeof NAMESPACES)[number];
export const DEFAULT_NS: Namespace = 'common';

export type Dictionary = Readonly<Record<string, string>>;
export type Resources = Readonly<Record<Lang, Readonly<Record<Namespace, Dictionary>>>>;

export const RESOURCES: Resources = {
  ko: {
    common: koCommon,
    labels: koLabels,
    admin: koAdmin,
    connect: koConnect,
    glossary: koGlossary,
    home: koHome,
    login: koLogin,
    logs: koLogs,
    people: koPeople,
    settings: koSettings,
    stats: koStats,
    app: koApp,
  },
  en: {
    common: enCommon,
    labels: enLabels,
    admin: enAdmin,
    connect: enConnect,
    glossary: enGlossary,
    home: enHome,
    login: enLogin,
    logs: enLogs,
    people: enPeople,
    settings: enSettings,
    stats: enStats,
    app: enApp,
  },
};

/** Keys removed on purpose: mockup-only wording (role switcher, demo states, first-visit tour, login demo box, "mockup" tags). */
export const EXCLUDED_MOCKUP_KEY_COUNT = 37;

/** One flat dictionary per language (all namespaces merged) — for code outside React and for tests. */
export function flatDictionary(lang: Lang): Record<string, string> {
  const out: Record<string, string> = {};
  for (const ns of NAMESPACES) Object.assign(out, RESOURCES[lang][ns]);
  return out;
}

/** Which namespace a key lives in (undefined when unknown). */
export function namespaceOf(key: string): Namespace | undefined {
  return NAMESPACES.find((ns) => Object.prototype.hasOwnProperty.call(RESOURCES.ko[ns], key));
}
